import {
  CommonRequest,
  CommonResponse,
  DmlResponse,
  Method,
  ServiceName,
} from 'models/common/RestApi';
import {
  MdIvestMdlEquipmentReviewResult,
  MdIvestMdlSpecComparison,
  MdIvestMdlCostSummary,
  MdIvestMdlTopSummary,
  MdIvestMdlTextSummary,
} from 'models/md/MdInvestResult';
import { callApi } from 'utils/ApiUtil';
import { MdLine } from 'models/md/MdLine';

/**
 * 형개발 투자비 산출 결과 > 초기화
 * @param items
 */
export const resetInvestResult = async (elmPrjId?: string, ivesMdlId?: string) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/md/invest/result/reset`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { elmPrjId: elmPrjId, ivesMdlId: ivesMdlId },
  };
  const response: CommonResponse<DmlResponse> = await callApi(request);
  return response;
};

/**
 * 형개발 투자비 산출 결과 > 모델 주요 사양 비교
 * @returns
 */
export const getMdIvestMdlSpecComparisonData = async (elmPrjId, ivesMdlId) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/md/invest/result/spec-comparison`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId,
    }),
  };
  const response: CommonResponse<MdIvestMdlSpecComparison[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as MdIvestMdlSpecComparison[];
};

/**
 * 형개발 투자비 산출 결과 > 생산 대상 설비 적합 여부 점검 결과 조회
 * @param elmPrjId 형개발 프로젝트 ID
 * @param ivesMdlId 검토 모델 ID
 * @returns 생산 대상 설비 적합 여부 점검 결과 데이터
 */
export const getMdIvestMdlEquipmentReviewResult = async (elmPrjId: string, ivesMdlId?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/md/invest/result/equipment-review`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId ?? '',
    }),
  };
  const response: CommonResponse<MdIvestMdlEquipmentReviewResult[]> = await callApi(request);
  return (
    response.successOrNot === 'Y' ? response.data : null
  ) as MdIvestMdlEquipmentReviewResult[];
};

/**
 * 형개발 투자비 산출 결과 > 생산 대상 설비 적합 여부 점검 결과 저장
 * @param items
 */
export const saveMdIvestMdlEquipmentReviewResult = async (
  elmPrjId?: string,
  ivesMdlId?: string,
  items?: MdIvestMdlEquipmentReviewResult[]
) => {
  const params =
    items?.map((item) => ({
      ...item,
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId,
    })) || [];

  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/md/invest/result/equipment-review`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: params,
  };
  const response: CommonResponse<DmlResponse> = await callApi(request);
  return response;
};

export const getMdIvestMdlCostSummaryData = async (elmPrjId: string, ivesMdlId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/md/invest/result/cost-summary`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId,
    }),
  };
  const response: CommonResponse<MdIvestMdlCostSummary[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as MdIvestMdlCostSummary[];
};

/**
 * 투자 산출결과 > 형개발 투자비 검토 결과 > 투자 결과 텍스트 요약 조회
 */
export const getMdInvestResultTextSummary = async (
  elmPrjId: string,
  ivesMdlId: string
): Promise<MdIvestMdlTextSummary | null> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/md/invest/result/text-summary`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId,
    }),
  };
  const response: CommonResponse<MdIvestMdlTextSummary> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as MdIvestMdlTextSummary;
};

/**
 * 투자 산출결과 > 형개발 투자비 검토 결과 > 투자 결과 텍스트 요약 저장
 * @param lines
 */
export const saveMdInvestResultTextSummary = async (condition?: any) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/md/invest/result/text-summary`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: condition,
  };
  const response: CommonResponse<DmlResponse> = await callApi(request);
  return response;
};

/**
 * 형개발 투자비 산출 결과 > 투자항목별 Top 10
 * @param elmPrjId 형개발 프로젝트 ID
 * @param ivesMdlId 검토 모델 ID
 * @returns 투자항목별 Top 10 데이터
 */
export const getMdIvestMdlTopSummaryData = async (elmPrjId: string, ivesMdlId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/md/invest/result/top-summary`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId,
    }),
  };
  const response: CommonResponse<MdIvestMdlTopSummary[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as MdIvestMdlTopSummary[];
};

/**
 * 형개발 투자비 산출 결과 > 타 Site 투자비 반영 내역
 * @param elmPrjId
 * @param ivesMdlId
 */
export const getMdInvestResultOtherSite = async (elmPrjId: string, ivesMdlId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/md/invest/result/other-site`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId,
    }),
  };
  const response: CommonResponse<MdIvestMdlTopSummary[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as MdIvestMdlTopSummary[];
};

/**
 * 투자 산출결과 > 투자비 요약 > 라인별 투자반영금액 목록 조회
 * @param elmPrjId
 * @param ivesMdlId
 */
export const getMdInvestLineList = async (elmPrjId, ivesMdlId) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/md/invest/result/line`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      ivesMdlId: ivesMdlId,
    }),
  };
  const response: CommonResponse<MdLine[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as MdLine[];
};

/**
 * 투자 산출결과 > 투자비 요약 > 라인별 투자반영금액 저장
 * @param lines
 */
export const saveMdInvestLineReflectionAmt = async (lines?: MdLine[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/md/invest/result/line`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: lines,
  };
  const response: CommonResponse<DmlResponse> = await callApi(request);
  return response;
};
